import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface DuplicateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  templateName: string;
  isOrgAdmin: boolean;
  onKeepPersonal: (name: string) => void | Promise<void>;
  onMakeOrg: (name: string) => void | Promise<void>;
}

export const DuplicateTemplateModal: React.FC<DuplicateTemplateModalProps> = ({
  open,
  onClose,
  templateName,
  isOrgAdmin,
  onKeepPersonal,
  onMakeOrg,
}) => {
  const [name, setName] = useState(`${templateName} (Copy)`);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(`${templateName} (Copy)`);
      setIsSubmitting(false);
    }
  }, [open, templateName]);

  const isNameValid = name.trim().length > 0;

  const handleKeepPersonal = async () => {
    setIsSubmitting(true);
    await onKeepPersonal(name.trim());
    setIsSubmitting(false);
  };

  const handleMakeOrg = async () => {
    setIsSubmitting(true);
    await onMakeOrg(name.trim());
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', maxWidth: 480, p: 0 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Duplicate template
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block' }}
          >
            Template name
          </Typography>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2.5 }}
            autoFocus
          />

          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: '8px',
              px: 2,
              py: 1.75,
              mb: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              This creates a <strong>personal copy</strong> with all sections and training
              included. Only you will be able to see and edit it. To share it with your
              clinicians, make it an organization template.
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ px: 3, pb: 3, pt: 2, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="text"
            color="inherit"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {isOrgAdmin && (
            <Button
              onClick={handleMakeOrg}
              variant="text"
              color="inherit"
              disabled={!isNameValid || isSubmitting}
              sx={{ fontWeight: 600 }}
            >
              Make org template
            </Button>
          )}
          <Button
            onClick={handleKeepPersonal}
            variant="contained"
            disabled={!isNameValid || isSubmitting}
            sx={{ fontWeight: 600 }}
          >
            Keep as personal
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
